/**
 * useRegisterArcheAI — an arche page's hookup to the GLOBAL Fias AI window.
 *
 * Replaces the per-page <FiasAIProvider capabilities={...}> wrapper: the
 * page calls this hook and its capabilities (actions, state snapshot,
 * applyAction) become the assistant's UI plane while the page is mounted.
 * The capabilities object is tracked through a ref updated every render,
 * so getStateSnapshot/applyAction are never stale; registration/
 * unregistration follows mount/unmount.
 *
 * Safe to call on pages rendered outside the provider (e.g. unauthenticated
 * embeds) — it no-ops when no global context is present.
 */

import { useEffect, useRef } from 'react';
import type { ArcheAICapabilities } from '../types';
import { useGlobalFiasAIOptional } from './GlobalFiasAIContext';

export function useRegisterArcheAI(capabilities: ArcheAICapabilities | null): void {
  const global = useGlobalFiasAIOptional();
  const capsRef = useRef<ArcheAICapabilities | null>(capabilities);
  capsRef.current = capabilities;

  const registerArche = global?.registerArche;
  const archeId = capabilities?.archeId ?? null;

  useEffect(() => {
    if (!registerArche || !archeId) return;
    // The registry stores a non-null ref; the wrapper below keeps reads
    // safe if the page nulls its capabilities while mounted.
    const stableRef = {
      get current() {
        return capsRef.current as ArcheAICapabilities;
      },
    } as React.MutableRefObject<ArcheAICapabilities>;
    return registerArche({ archeId, capsRef: stableRef });
  }, [registerArche, archeId]);
}
